import isBoolean from 'lodash/isBoolean';
import { captureException } from '@sentry/react';
import { useSelector } from 'react-redux';
import { getLocalStorage, noop, setLocalStorage } from '../utils';
import { useFirebase } from '../utils/firebase/FirebaseProvider';
import { FIRESTORE_COLLECTIONS } from '../utils/firebase/constants';
import useDispatchFactory from './useDispatchFactory';
import {
  updatePreferences as updatePreferencesAction,
  setPreferences as setPreferencesAction
} from '../redux/modules/preferences/actions';

const matchMedia = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');

const getInitialDrawerState = () => {
  const { innerWidth } = window;
  const savedState = getLocalStorage('drawer');
  const hasSavedState = isBoolean(savedState);
  // making drawer closed by default in tablets
  if (innerWidth < 768 && !hasSavedState) {
    return false;
  }
  return hasSavedState ? savedState : true;
};

export const getDefaultPreferences = () => ({
  autoAssignOnFirstReply: true,
  autoAssignOnNewConversation: true,
  startTimerOnReply: true,
  autoChangeStatusOnFirstReply: false,
  autoChangeStatus: null,
  isDrawerOpen: getInitialDrawerState(),
  isEditorModeOn: false,
  autoDetectTheme: true,
  activeTheme: matchMedia?.matches ? 'dark' : 'light',
  openTimepadOnStartTimer: false,
  playSoundOnNewMessage: false,
  playSoundOnNewThread: true,
  stopTimerOnCloseThread: true,
  sendReminderOfTimeEntry: true
});

const fallbackValue = {
  isDrawerOpen: getInitialDrawerState()
};

export default function usePreferences() {
  const setPreferences = useDispatchFactory(setPreferencesAction);
  const updatePreferences = useDispatchFactory(updatePreferencesAction);
  const preferences = useSelector(state => state.preferences.data);

  const { getDocuments, createDocument, updateDocument } = useFirebase();
  const { preferences: collectionName } = FIRESTORE_COLLECTIONS;

  const reset = () => {
    setPreferences(null);
  };

  const createPreferencesDocument = payload => {
    createDocument(payload, { collectionName })
      .then(({ id }) => {
        setPreferences({
          ...payload,
          id
        });
      })
      .catch(noop);
  };

  const handleChange = (
    data,
    { shouldSyncWithFirebase = true } = {},
    onSuccess = noop,
    onError = noop
  ) => {
    const allData = {
      ...preferences,
      ...data
    };
    const payload = {
      autoAssignOnFirstReply: allData.autoAssignOnFirstReply,
      autoAssignOnNewConversation: allData.autoAssignOnNewConversation,
      startTimerOnReply: allData.startTimerOnReply,
      autoChangeStatusOnFirstReply: allData.autoChangeStatusOnFirstReply,
      autoChangeStatus: allData.autoChangeStatus,
      activeTheme: allData.activeTheme,
      autoDetectTheme: allData.autoDetectTheme,
      isDrawerOpen: allData.isDrawerOpen,
      isEditorModeOn: allData.isEditorModeOn,
      openTimepadOnStartTimer: allData.openTimepadOnStartTimer,
      playSoundOnNewMessage: allData.playSoundOnNewMessage,
      playSoundOnNewThread: allData.playSoundOnNewThread,
      sendReminderOfTimeEntry: allData.sendReminderOfTimeEntry,
      stopTimerOnCloseThread: allData.stopTimerOnCloseThread
    };
    setLocalStorage({
      drawer: allData.isDrawerOpen
    });
    updatePreferences(allData);
    if (shouldSyncWithFirebase && allData?.id) {
      updateDocument(payload, {
        id: allData.id,
        collectionName
      })
        .then(onSuccess)
        .catch(err => {
          captureException(err);
          if (onError) onError();
        });
    } else if (onError) onError();
  };

  const toggleDrawer = customState => {
    const isDrawerOpen = isBoolean(customState) ? customState : !preferences?.isDrawerOpen;
    handleChange({ isDrawerOpen });
  };

  const toggleEditor = () => {
    handleChange({ isEditorModeOn: !preferences?.isEditorModeOn });
  };

  const setupPreferences = async ({
    shouldSetDefaultPreferences = false,
    overwritePreferenceValues
  } = {}) => {
    const defaultState = getDefaultPreferences();
    if (shouldSetDefaultPreferences) {
      setPreferences({ ...defaultState, ...overwritePreferenceValues });
      return;
    }
    if (!preferences) {
      try {
        const savedPreference = await getDocuments({
          collectionName,
          returnOnlyFirst: true
        });
        if (savedPreference) {
          setPreferences({ ...defaultState, ...savedPreference, ...overwritePreferenceValues });
        } else {
          setPreferences({ ...defaultState, ...overwritePreferenceValues });
          createPreferencesDocument(defaultState);
        }
        setLocalStorage({
          drawer: (savedPreference || defaultState).isDrawerOpen
        });
      } catch (err) {
        handleChange({ ...defaultState, ...overwritePreferenceValues });
        captureException(err);
      }
    }
  };

  return {
    preferences: preferences || fallbackValue,
    updatePreferences: handleChange,
    toggleDrawer,
    toggleEditor,
    setupPreferences,
    reset
  };
}
